import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiTrash2, FiExternalLink, FiAlertCircle, FiEdit2, FiX, FiSave } from 'react-icons/fi';

const EMPTY_TENANT = {
  company_name: '',
  dba_name: '',
  ffl_number: '',
  address: '',
  phone: '',
  email: '',
  primary_contact_name: '',
  primary_contact_email: '',
};

export default function Tenants() {
  const { user, enterTenant } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newTenant, setNewTenant] = useState({ ...EMPTY_TENANT });
  const [editForm, setEditForm] = useState({ ...EMPTY_TENANT });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTenants();
      setTenants(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load tenants. Please ensure your profile is set up as SUPER_ADMIN.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    setError(null);
    try {
      await api.createTenant(newTenant, user.id);
      setIsAdding(false);
      setNewTenant({ ...EMPTY_TENANT });
      fetchTenants();
      window.location.reload();
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to create tenant. check RLS policies.");
    }
  };

  const handleEdit = (tenant) => {
    setEditingId(tenant.id);
    setEditForm({
      company_name: tenant.company_name || '',
      dba_name: tenant.dba_name || '',
      ffl_number: tenant.ffl_number || '',
      address: tenant.address || '',
      phone: tenant.phone || '',
      email: tenant.email || '',
      primary_contact_name: tenant.primary_contact_name || '',
      primary_contact_email: tenant.primary_contact_email || '',
    });
    setIsAdding(false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ ...EMPTY_TENANT });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await api.updateTenant(editingId, editForm);
      setEditingId(null);
      fetchTenants();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to update tenant.');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (tenant) => {
    setDeleteTarget(tenant);
    setDeleteConfirmText('');
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirmText('');
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmText !== 'DELETE' || !deleteTarget || deleting) return;
    setError(null);
    setDeleting(true);
    try {
      await api.deleteTenant(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      fetchTenants();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to delete tenant.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const renderTenantForm = (formData, setFormData, onSubmit, onCancel, submitLabel, isLoading) => (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input
        label="Company Name"
        value={formData.company_name}
        onChange={e => setFormData({ ...formData, company_name: e.target.value })}
        required
      />
      <Input
        label="DBA Name"
        placeholder="Doing business as (optional)"
        value={formData.dba_name}
        onChange={e => setFormData({ ...formData, dba_name: e.target.value })}
      />
      <Input
        label="FFL Number"
        placeholder="X-XX-XXX-XX-XX-XXXXX"
        value={formData.ffl_number}
        onChange={e => setFormData({ ...formData, ffl_number: e.target.value })}
        required
      />
      <Input
        label="Business Address"
        value={formData.address}
        onChange={e => setFormData({ ...formData, address: e.target.value })}
        required
      />
      <Input
        label="Phone"
        type="tel"
        placeholder="(555) 555-5555"
        value={formData.phone}
        onChange={e => setFormData({ ...formData, phone: e.target.value })}
      />
      <Input
        label="Business Email"
        type="email"
        value={formData.email}
        onChange={e => setFormData({ ...formData, email: e.target.value })}
        required
      />
      <Input
        label="Primary Contact Name"
        placeholder="(optional)"
        value={formData.primary_contact_name}
        onChange={e => setFormData({ ...formData, primary_contact_name: e.target.value })}
      />
      <Input
        label="Primary Contact Email"
        type="email"
        placeholder="(optional)"
        value={formData.primary_contact_email}
        onChange={e => setFormData({ ...formData, primary_contact_email: e.target.value })}
      />
      <div className="md:col-span-2 flex justify-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" isLoading={isLoading} icon={FiSave}>{submitLabel}</Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Tenant Management</h2>
          <p className="text-sm text-slate-500">Manage FFL businesses and platform access.</p>
        </div>
        <Button onClick={() => { setIsAdding(true); setEditingId(null); }} icon={FiPlus}>Add New Tenant</Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/30">
          <SafeIcon icon={FiAlertCircle} className="h-5 w-5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {isAdding && (
        <Card className="border-primary-200 dark:border-primary-900 shadow-lg">
          <CardHeader>
            <CardTitle>Register New FFL Tenant</CardTitle>
          </CardHeader>
          <CardContent>
            {renderTenantForm(
              newTenant,
              setNewTenant,
              handleCreate,
              () => setIsAdding(false),
              'Create Tenant & Profile',
              false
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading tenants...</div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-lg border border-dashed border-slate-300 dark:border-slate-800">
            <p className="text-slate-500">No tenants registered yet. Create your first one to get started.</p>
          </div>
        ) : (
          tenants.map(tenant => (
            <Card key={tenant.id} className="hover:border-primary-300 transition-colors">
              <CardContent className="p-6">
                {editingId === tenant.id ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">Edit Tenant</h3>
                      <button onClick={handleCancelEdit} className="text-slate-400 hover:text-slate-600">
                        <SafeIcon icon={FiX} className="h-5 w-5" />
                      </button>
                    </div>
                    {renderTenantForm(
                      editForm,
                      setEditForm,
                      handleSaveEdit,
                      handleCancelEdit,
                      'Save Changes',
                      saving
                    )}
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold">{tenant.company_name}</h3>
                        {tenant.dba_name && (
                          <span className="text-sm text-slate-500">DBA: {tenant.dba_name}</span>
                        )}
                        <Badge variant={tenant.is_active ? 'success' : 'default'}>
                          {tenant.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm font-mono text-slate-500">FFL: {tenant.ffl_number}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{tenant.address}</p>
                      {(tenant.phone || tenant.email) && (
                        <p className="text-sm text-slate-500">
                          {tenant.phone && <span>{tenant.phone}</span>}
                          {tenant.phone && tenant.email && <span className="mx-2">|</span>}
                          {tenant.email && <span>{tenant.email}</span>}
                        </p>
                      )}
                      {(tenant.primary_contact_name || tenant.primary_contact_email) && (
                        <p className="text-sm text-slate-400">
                          Contact: {tenant.primary_contact_name}
                          {tenant.primary_contact_email && ` (${tenant.primary_contact_email})`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="secondary"
                        onClick={() => handleEdit(tenant)}
                        icon={FiEdit2}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => { enterTenant(tenant); navigate('/'); }}
                        icon={FiExternalLink}
                      >
                        Enter Tenant
                      </Button>
                      <Button variant="ghost" className="text-red-600" onClick={() => openDeleteModal(tenant)}>
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* ── Delete-tenant confirmation modal ── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900">
            <div className="flex items-start gap-3 mb-4">
              <SafeIcon icon={FiAlertCircle} className="h-6 w-6 text-red-600 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Delete {deleteTarget.company_name}?
                </h3>
                <p className="text-sm text-slate-500">
                  This permanently deletes the tenant and <strong>all</strong> of its data:
                  bound book entries, firearms, incoming orders, contacts, lookups,
                  documents, audit logs, API keys, and user profiles. This cannot be undone.
                </p>
              </div>
            </div>
            <Input
              label='Type DELETE to confirm'
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && deleteConfirmText === 'DELETE') handleConfirmDelete(); }}
            />
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={closeDeleteModal} disabled={deleting}>Cancel</Button>
              <Button
                variant="danger"
                onClick={handleConfirmDelete}
                isLoading={deleting}
                disabled={deleting || deleteConfirmText !== 'DELETE'}
                icon={FiTrash2}
              >
                Delete Tenant
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
